// src/components/Team.jsx
//
// The owner's conciergerie team (documentations/conciergerie-plan.md): add
// managers / agents, change their role, deactivate them, regenerate their
// personal link. Members aren't Keycloak users - their link is e-mailed to
// them, and shown here once so it can be handed over another way.
// Backed by backend/routes/staff.ts.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Paper, Stack, Chip, Alert, TextField,
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { initKeycloak } from '../keycloak';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
const EMPTY = { name: '', email: '', role: 'AGENT' };

export default function Team() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [staff, setStaff] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [link, setLink] = useState(null); // { name, url } - shown once
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      const kc = await initKeycloak();
      if (!kc.authenticated) return kc.login();
      setToken(kc.token ?? '');
    })().catch(console.error);
  }, []);

  const api = useCallback(async (path, options = {}) => {
    const r = await fetch(`${API_BASE_URL}/v2/staff${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw Object.assign(new Error(data.error || `HTTP ${r.status}`), { status: r.status });
    return data;
  }, [token]);

  const load = useCallback(() => {
    if (!token) return;
    api('').then((d) => setStaff(d.staff || [])).catch(() => setError(t('team.loadError')));
  }, [token, api, t]);
  useEffect(() => { load(); }, [load]);

  const showLink = (name, url) => { setLink({ name, url }); setCopied(false); };

  const add = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const d = await api('', { method: 'POST', body: JSON.stringify(form) });
      showLink(d.staff.name, d.accessLink);
      setForm(EMPTY);
      load();
    } catch (err) {
      setError(err.status === 409 ? t('team.duplicate') : t('team.saveError'));
    }
  };

  const update = async (m, changes) => {
    setError('');
    try {
      await api(`/${m.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: m.name, email: m.email, role: m.role, active: Boolean(m.active), ...changes }),
      });
      load();
    } catch {
      setError(t('team.saveError'));
    }
  };

  const newLink = async (m) => {
    if (!window.confirm(t('team.newLinkConfirm', { name: m.name }))) return;
    try {
      const d = await api(`/${m.id}/link`, { method: 'POST' });
      showLink(m.name, d.accessLink);
    } catch {
      setError(t('team.saveError'));
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Button variant="text" onClick={() => navigate('/alerts')} sx={{ mb: 1 }}>← {t('team.back')}</Button>
      <Typography variant="h4" sx={{ mb: 1 }}>{t('team.title')}</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>{t('team.intro')}</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {link && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setLink(null)}>
          <Typography variant="body2" sx={{ mb: 1 }}>{t('team.linkSent', { name: link.name })}</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box component="code" sx={{ p: 1, bgcolor: '#fff', borderRadius: 1, fontSize: 13, wordBreak: 'break-all' }}>{link.url}</Box>
            <Button size="small" variant="outlined"
              onClick={() => navigator.clipboard.writeText(link.url).then(() => setCopied(true), () => {})}>
              {copied ? t('team.copied') : t('team.copy')}
            </Button>
          </Box>
        </Alert>
      )}

      <Paper variant="outlined" component="form" onSubmit={add} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{t('team.add')}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField size="small" required label={t('team.name')} value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <TextField size="small" required type="email" label={t('team.email')} value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} sx={{ flexGrow: 1 }} />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('team.role')}</InputLabel>
            <Select label={t('team.role')} value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
              <MenuItem value="AGENT">{t('team.roles.AGENT')}</MenuItem>
              <MenuItem value="MANAGER">{t('team.roles.MANAGER')}</MenuItem>
            </Select>
          </FormControl>
          <Button type="submit" variant="contained">{t('team.addButton')}</Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{t('team.rolesHelp')}</Typography>
      </Paper>

      <Stack spacing={1.5}>
        {staff && staff.length === 0 && <Typography color="text.secondary">{t('team.empty')}</Typography>}
        {staff && staff.map((m) => (
          <Paper key={m.id} variant="outlined" sx={{ p: 2, opacity: m.active ? 1 : 0.55 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography sx={{ fontWeight: 700 }}>{m.name}</Typography>
                <Typography variant="body2" color="text.secondary">{m.email}</Typography>
              </Box>
              <Select size="small" value={m.role} onChange={(e) => update(m, { role: e.target.value })} disabled={!m.active}>
                <MenuItem value="AGENT">{t('team.roles.AGENT')}</MenuItem>
                <MenuItem value="MANAGER">{t('team.roles.MANAGER')}</MenuItem>
              </Select>
              {!m.active && <Chip size="small" label={t('team.inactive')} />}
              {m.active ? (
                <>
                  <Button size="small" onClick={() => newLink(m)}>{t('team.newLink')}</Button>
                  <Button size="small" color="error" onClick={() => update(m, { active: false })}>{t('team.deactivate')}</Button>
                </>
              ) : (
                <Button size="small" onClick={() => update(m, { active: true })}>{t('team.reactivate')}</Button>
              )}
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Container>
  );
}
